import { action, computed, observable } from 'mobx';
import {
  MAX_TIMER_DURATION,
  RECORDING_STATUS,
  TIMER_INTERVAL,
  ZERO_VALUE
} from '../../constants/constants';
import { formatTimer } from '../helpers/recorder.helpers';
import AudioRecordJsService from '../../common/services/audio-recordJs.service';
import commonStore from '../../common/stores/common.store';

/**
 * Class representing the recorder store.
 * */
export class RecorderStore {
  @observable audio = null;

  @observable duration = ZERO_VALUE;

  @observable progress = ZERO_VALUE;

  @observable recordBlob = null;

  @observable status = RECORDING_STATUS.WAITING;

  timer = null;

  /**
   * Formats the duration.
   * @return {string} duration - The formatted duration.
   * */
  @computed get parseTimer() {
    return formatTimer(this.duration);
  }

  /**
   * Formats the progress.
   * @return {string} progress - The formatted progress.
   * */
  @computed get width() {
    return `${this.progress}%`;
  }

  /**
   * Sets the current status.
   * @param {string} status - The status.
   * */
  @action setStatus = (status) => {
    this.status = status;
  };

  /**
   * Sets the record blob.
   * @param {object} blob - The file bulb.
   * */
  setRecordBlob = (blob) => {
    this.recordBlob = blob;
  };

  /**
   * Sets the current audio.
   * @param {object} audio - The audio.
   * */
  @action setAudio = (audio) => {
    this.audio = audio;
    this.duration = Math.floor(audio.duration);
  };

  /**
   * Plays the audio.
   * */
  @action playAudio = () => {
    this.audio.play();
    this.setStatus(RECORDING_STATUS.PLAYING);
  };

  /**
   * Pause the audio.
   * */
  @action pauseAudio = () => {
    this.audio.pause();
    this.setStatus(RECORDING_STATUS.RECORDED);
    this.clearProgress();
  };

  /**
   * Starts the recording timer.
   * */
  @action startTimer = async () => {
    try {
      this.resetAudio();
      this.setRecordBlob(null);

      //moved to AFTER startRecording() starts to eliminate audio being cut off when recording starts
      //this.setStatus(RECORDING_STATUS.RECORDING);

      this.duration = ZERO_VALUE;
      await AudioRecordJsService.startRecording();
      await this.sleep(100); //sleep for a tenth of a second to give buffer to recording
      commonStore.hideSpinner();
      //RECORDING_STATUS gets set only once startRecording() initiates and a 1 tenth second buffer is added
      this.setStatus(RECORDING_STATUS.RECORDING);

      this.timer = setInterval(() => {
        this.duration = this.duration + 1;
        if (this.duration === MAX_TIMER_DURATION) {
          this.clearTimer();
        }
      }, TIMER_INTERVAL);
    } catch (error) {
      this.setStatus(RECORDING_STATUS.ERROR);
    }
  };

  /**
   * Create a delay for something like the beginning of recordings
   * */
  @action sleep = m => new Promise(r => setTimeout(r, m))

  /**
   * Removes the recording timer.
   * */
  @action clearTimer = async () => {
    await AudioRecordJsService.stopRecording(this.setRecordBlob);
    if (this.timer) {
      clearInterval(this.timer);
      this.duration = ZERO_VALUE;
      this.setStatus(RECORDING_STATUS.RECORDED);
    }
  };

  /**
   * Resets the audio.
   * */
  @action resetAudio = () => {
    this.clearProgress();
    this.progress = ZERO_VALUE;
    if (this.audio) {
      this.audio.currentTime = ZERO_VALUE;
    }
  };

  /**
   * Updates the audio playing progress.
   * */
  @action updateProgress = () => {
    const { currentTime, duration } = this.audio;
    const increment = 10 / duration;
    const percent = Math.min(increment * currentTime * 10, 100);

    this.progress = percent;
    this.duration = Math.floor(currentTime);
    if (percent <= 100) {
      this.timer = setTimeout(() => this.updateProgress(), 100);
    }
  };

  /**
   * Clears the progress timer.
   * */
  clearProgress = () => {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  };

}

export default new RecorderStore();



// WEBPACK FOOTER //
// ./src/messages/stores/recorder.store.js